import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { JoinLinkDayResponse } from "@/types/generated/cms.generated";
import { num } from "@/components/join-links/joinLinkFormat";

/** One chart row per server day. `date` is the server's UAE day key, kept exactly as sent. */
export interface JoinLinkChartRow {
  date: string;
  label: string;
  ios: number;
  android: number;
  employeesJoined: number;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2026-10-31" ⇒ "31 Oct", by splitting the string — never `new Date(str)`, which reads it as
 * UTC midnight and shows the previous day west of UTC (C-DAY). Anything else is returned as-is.
 */
export function uaeDayLabel(day: string | null | undefined): string {
  if (!day) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  if (!match || !month) return day;
  return `${Number(match[3])} ${month}`;
}

/** The server's days as chart rows, in the server's order, with each `date` key unchanged. */
export function toChartRows(days: JoinLinkDayResponse[]): JoinLinkChartRow[] {
  return days.map((day) => ({
    date: day.date ?? "",
    label: uaeDayLabel(day.date),
    ios: num(day.ios),
    android: num(day.android),
    employeesJoined: num(day.employeesJoined),
  }));
}

// Colours are ds tokens only (light + dark follow the theme).
const IOS = "hsl(var(--ds-accent))";
const ANDROID = "hsl(var(--ds-ink3) / 0.55)";
const JOINED = "hsl(var(--ds-pink))";
const AXIS = "hsl(var(--ds-ink3))";
const GRID = "hsl(var(--ds-line))";

interface JoinLinkDailyChartProps {
  days: JoinLinkDayResponse[];
}

/** Day-by-day chart: stacked iPhone + Android opens, Employees joined as a line. Days in UAE time. */
export default function JoinLinkDailyChart({ days }: JoinLinkDailyChartProps) {
  const rows = toChartRows(days);
  const allZero = rows.every((r) => r.ios === 0 && r.android === 0 && r.employeesJoined === 0);

  return (
    <figure className="m-0" data-testid="join-link-chart">
      <div className="relative h-[180px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 4, bottom: 0, left: -18 }}>
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={uaeDayLabel}
              tick={{ fill: AXIS, fontSize: 11 }}
              axisLine={{ stroke: GRID }}
              tickLine={false}
              minTickGap={12}
            />
            <YAxis allowDecimals={false} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
            <Tooltip
              labelFormatter={(value) => uaeDayLabel(String(value))}
              cursor={{ fill: "hsl(var(--ds-sunk))" }}
              contentStyle={{
                background: "hsl(var(--ds-panel))",
                border: "1px solid hsl(var(--ds-line))",
                borderRadius: 8,
                color: "hsl(var(--ds-ink))",
                fontSize: 12,
              }}
              labelStyle={{ color: "hsl(var(--ds-ink))", fontWeight: 600 }}
            />
            <Bar dataKey="ios" name="iPhone opens" stackId="opens" fill={IOS} isAnimationActive={false} />
            <Bar dataKey="android" name="Android opens" stackId="opens" fill={ANDROID} isAnimationActive={false} />
            <Line
              dataKey="employeesJoined"
              name="Employees joined"
              type="monotone"
              stroke={JOINED}
              strokeWidth={2}
              dot={{ r: 2.6, fill: JOINED, stroke: JOINED }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        {allZero && (
          <span className="pointer-events-none absolute inset-0 grid place-items-center text-[13px] text-ds-ink3">
            No opens yet
          </span>
        )}
      </div>
      <figcaption
        className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-ds-ink2"
        data-testid="join-link-chart-legend"
      >
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-ds-accent" aria-hidden="true" />
          iPhone opens
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm bg-ds-ink3/55" aria-hidden="true" />
          Android opens
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-0.5 w-3 rounded-full bg-ds-pink" aria-hidden="true" />
          Employees joined
        </span>
        <span className="text-ds-ink3">Days in UAE time</span>
      </figcaption>
    </figure>
  );
}
